/**
 * Minimal helpers to validate action payload fields.
 * We keep it tiny and framework-agnostic.
 */

import { ProtocolError } from "../../validation/ProtocolError";

export class ActionPayloadValidationError extends ProtocolError {
    public readonly actionTypeName: string;
    public readonly fieldName: string;
  
    public constructor(actionTypeName: string, fieldName: string, message: string) {
      super(`Action payload validation failed for "${actionTypeName}.${fieldName}": ${message}`);
      this.name = "ActionPayloadValidationError";
      this.actionTypeName = actionTypeName;
      this.fieldName = fieldName;
    }
  }
  
  export function requireStringField(
    actionTypeName: string,
    payload: Record<string, unknown>,
    fieldName: string
  ): string {
    const value = payload[fieldName];
    if (typeof value !== "string" || value.length === 0) {
      throw new ActionPayloadValidationError(actionTypeName, fieldName, "Must be a non-empty string.");
    }
    return value;
  }
  
  /**
   * A monetary payload field: a non-negative integer string in minor units. The value is coerced
   * with BigInt() downstream, so a decimal, grouped or exponent-formatted string has to be refused
   * here as a ProtocolError rather than throwing a bare SyntaxError from outside any validator.
   */
  export function requireMinorAmountField(
    actionTypeName: string,
    payload: Record<string, unknown>,
    fieldName: string
  ): string {
    const value = requireStringField(actionTypeName, payload, fieldName);
    if (!/^\d+$/.test(value)) {
      throw new ActionPayloadValidationError(actionTypeName, fieldName, `Must be a non-negative integer string in minor units, got "${value}".`);
    }
    return value;
  }

  /** A monetary payload field that may be absent; when present it follows `requireMinorAmountField`. */
  export function optionalMinorAmountField(
    actionTypeName: string,
    payload: Record<string, unknown>,
    fieldName: string
  ): string | undefined {
    if (payload[fieldName] === undefined || payload[fieldName] === null) return undefined;
    return requireMinorAmountField(actionTypeName, payload, fieldName);
  }

  export function optionalStringField(payload: Record<string, unknown>, fieldName: string): string | undefined {
    const value = payload[fieldName];
    if (typeof value === "string" && value.length > 0) return value;
    return undefined;
  }

  export function requireStringArrayField(
    actionTypeName: string,
    payload: Record<string, unknown>,
    fieldName: string
  ): string[] {
    const value = payload[fieldName];
    if (!Array.isArray(value)) {
      throw new ActionPayloadValidationError(actionTypeName, fieldName, "Must be an array of strings.");
    }
  
    for (let i = 0; i < value.length; i++) {
      const item = value[i];
      if (typeof item !== "string" || item.length === 0) {
        throw new ActionPayloadValidationError(
          actionTypeName,
          `${fieldName}[${i}]`,
          "Must be a non-empty string."
        );
      }
    }
  
    return value as string[];
  }